import { ListResult } from '../fetchers/ListResult';
import { ISliceable } from '../models/ISliceable';
import { MaxRequestArrayLength } from '../services/ServerService';
import { RequestHelper } from './RequestHelper';
import { ResultHelper } from './ResultHelper';

export module SliceableHelper {

  export let BlockSize = MaxRequestArrayLength;

  // Return true from itemsReceived to cancel.
  export function fetchAll<T>(sliceable: ISliceable<T>, itemsReceived?: (items: T[]) => boolean): ng.IPromise<ListResult<T>> {
    let totalList: T[] = [];

    const getBlock = (blockStartIndex: number = 0): ng.IPromise<ListResult<T>> => {
      return sliceable.getSlice(blockStartIndex, BlockSize).then((result: ListResult<T>): any => { // Should return either ng.IPromise<ListResult<T>> or ListResult<T>
        let cancel = false;
        if (itemsReceived) {
          cancel = !!itemsReceived(result.items);
        }
        totalList = totalList.concat(result.items);
        if(!cancel && result.total > totalList.length) {
          return getBlock(totalList.length);
        }
        return new ListResult(totalList);
      });
    };

    return getBlock();
  }

  /**
   * This helper allows for fetching all items for a query where one of the parameters is an array
   * filter (e.g. fetch all data connections where data source ID is IN a given list of IDs).
   * For this kind of query (particularly where there's a one-to-many relationship between the input
   * array param values and the returned objects), we need to break down the request in two ways:
   *   1. Break the request's array param into chunks of 1000 items to pass into the API;
   *   2. Fetch multiple pages of result items (a sliceable query typically returns 100 at a time).
   *
   * Because the sliceable query is being distributed over multiple chunks and reassembled after the
   * query is executed, this method does not guarantee that sort parameters in the original query
   * will be honored in the final list.
   */
  export function fetchAllForSliceableWithArrayParam<TArrayParam, T>(
    arrayParamValues: TArrayParam[],
    makeSliceableFromArrayParamBlock: (arrayParamValues: TArrayParam[]) => ISliceable<T>,
    $q: ng.IQService
  ): ng.IPromise<T[]> {

    const arrayParamBlocks = RequestHelper.makeBlocks(arrayParamValues, RequestHelper.DefaultBlockSize);

    const resultPromisesPerBlock: ng.IPromise<ListResult<T>>[] = arrayParamBlocks.map(block => {
      const sliceable = makeSliceableFromArrayParamBlock(block);
      return SliceableHelper.fetchAll(sliceable);
    });

    return $q.all(resultPromisesPerBlock).then(ResultHelper.combineListResultItems);
  }

}
